import type {
  DailyStats,
  DevelopmentalNorm,
  InsightSnapshot,
  LogEvent,
  Status,
  Task
} from "../types/domain";
import { getNormForWeek } from "../../data/norms";
import { getScheduledVaccines, getScheduleWeekForAge } from "../../data/vaxSchedules";

interface Input {
  stats: DailyStats;
  norm: DevelopmentalNorm;
  markers: LogEvent[];
  growthVelocityGPerWeek: number | null;
  feedingMode?: string;
  tasks?: Task[];
  vaccineLogs?: LogEvent[];
  babyAgeWeeks?: number;
  countryCode?: string;
}

function toPercent(actual: number, expected: number): number {
  if (!expected) return 0;
  return Math.round(((actual - expected) / expected) * 100);
}

export function calculateStatus({
  stats,
  norm,
  markers,
  growthVelocityGPerWeek,
  feedingMode,
  tasks,
  vaccineLogs,
  babyAgeWeeks,
  countryCode
}: Input): InsightSnapshot {
  const feedPct = toPercent(stats.feedTotalMl, norm.feedingTargetMl);
  const diaperTotal = stats.diaperCountWet + stats.diaperCountSoiled;
  const diaperPct = toPercent(diaperTotal, norm.diaperMin);
  const markerTexts = markers.map((m) => m.payload.markerText ?? "Health marker");
  const suppressFeedAlert = feedingMode === "nursing" || feedingMode === "both";

  let status: Status = "normal";
  if (markerTexts.length > 0) {
    status = "alert";
  } else if ((!suppressFeedAlert && feedPct <= -30) || diaperPct <= -30) {
    status = "alert";
  } else if ((!suppressFeedAlert && feedPct <= -20) || diaperPct <= -20) {
    status = "attention";
  }

  if (
    growthVelocityGPerWeek !== null &&
    (growthVelocityGPerWeek < norm.growthMinGPerWeek ||
      growthVelocityGPerWeek > norm.growthMaxGPerWeek)
  ) {
    status = status === "alert" ? "alert" : "attention";
  }

  const reasons: string[] = [];
  if (markerTexts.length > 0) {
    reasons.push(...markerTexts.map((t) => `Health marker: ${t}`));
  }
  if (!suppressFeedAlert && feedPct <= -20) {
    reasons.push(`Feeding is ${Math.abs(feedPct)}% below target`);
  }
  if (diaperPct <= -20) {
    reasons.push(`Diaper changes are ${Math.abs(diaperPct)}% below minimum`);
  }
  if (
    growthVelocityGPerWeek !== null &&
    (growthVelocityGPerWeek < norm.growthMinGPerWeek ||
      growthVelocityGPerWeek > norm.growthMaxGPerWeek)
  ) {
    reasons.push(`Growth velocity outside expected range (${growthVelocityGPerWeek} g/week)`);
  }

  let summaryText: string;
  if (status === "normal") {
    summaryText = "Today's logs are within expected developmental ranges.";
  } else if (reasons.length > 0) {
    summaryText = reasons.join(". ") + ".";
  } else {
    summaryText =
      status === "attention"
        ? "Some metrics are outside expected range. Monitor closely."
        : "Alert level reached due to red flags or major metric deviation.";
  }

  const snapshot: InsightSnapshot = {
    status,
    summaryText,
    headsUp: norm.headsUp,
    milestonesExpected: norm.milestones,
    redFlags: markerTexts,
    deviation: {
      feedPct,
      diaperPct,
      growthGPerWeek: growthVelocityGPerWeek
    }
  };

  // Task overdue logic
  const now = Date.now();
  const overdue = (tasks ?? []).filter((t) => new Date(t.nextDueAt).getTime() < now);
  const pendingWithinHour = (tasks ?? []).filter((t) => {
    const due = new Date(t.nextDueAt).getTime();
    return due >= now && due <= now + 3600000;
  });
  snapshot.pendingTasksCount = pendingWithinHour.length;
  snapshot.overdueTasksCount = overdue.length;
  if (overdue.length > 0) {
    if (snapshot.status === "normal") snapshot.status = "attention";
    const names = overdue.slice(0, 2).map((t) => t.title).join(", ");
    snapshot.summaryText += ` ${overdue.length} task${overdue.length > 1 ? "s" : ""} overdue (${names}).`;
  }

  // Vaccination heads-up logic
  if (babyAgeWeeks !== undefined && countryCode) {
    const currentWeekVaccines = getScheduledVaccines(countryCode, babyAgeWeeks);
    const currentScheduleWeek = getScheduleWeekForAge(countryCode, babyAgeWeeks);
    const loggedVaccineIdsThisWeek = (vaccineLogs ?? [])
      .filter((l) => l.payload.vaccineWeek === currentScheduleWeek)
      .flatMap((l) => l.payload.vaccineIds ?? []);
    const missingThisWeek = currentWeekVaccines.filter((v) => !loggedVaccineIdsThisWeek.includes(v));

    const recentVaccineLog = (vaccineLogs ?? []).find((l) => {
      const diffH = (Date.now() - new Date(l.occurredAt).getTime()) / 3600000;
      return diffH <= 24;
    });

    if (recentVaccineLog) {
      snapshot.headsUp = "Vaccines logged. Monitor for mild fever or tenderness at the injection site today.";
    } else if (missingThisWeek.length > 0) {
      snapshot.headsUp = `⚠️ Vaccination milestone: ${missingThisWeek.join(", ")} ${missingThisWeek.length === 1 ? "is" : "are"} due this week. Tap to log.`;
    }
  }

  return snapshot;
}

export function calculateWeightPercentile(
  weightG: number,
  ageWeeks: number,
  sex: "boy" | "girl"
): string {
  const norm = getNormForWeek(ageWeeks);
  const data = sex === "boy" ? norm.boys : norm.girls;
  if (!data) return "—";
  const weightKg = weightG / 1000;
  if (weightKg >= data.p95) return "95th+";
  if (weightKg >= data.p50) return "50th–95th";
  if (weightKg >= data.p40) return "40th–50th";
  if (weightKg >= data.p30) return "30th–40th";
  if (weightKg >= data.p20) return "20th–30th";
  if (weightKg >= data.p10) return "10th–20th";
  return "Below 10th";
}

export function getWeightStalenessDays(ageWeeks: number): number {
  if (ageWeeks < 4) return 7;
  if (ageWeeks < 13) return 14;
  if (ageWeeks < 26) return 21;
  if (ageWeeks < 52) return 35;
  return 60;
}

export function calculateGrowthVelocity(entries: LogEvent[]): number | null {
  const weightEntries = entries
    .filter((event) => event.type === "weight" && event.payload.weightG)
    .sort(
      (a, b) =>
        new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime()
    );

  if (weightEntries.length < 2) return null;

  const previous = weightEntries[weightEntries.length - 2];
  const latest = weightEntries[weightEntries.length - 1];
  const diffMs =
    new Date(latest.occurredAt).getTime() - new Date(previous.occurredAt).getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  if (diffDays <= 0) return null;

  return Number(
    ((((latest.payload.weightG ?? 0) - (previous.payload.weightG ?? 0)) / diffDays) *
      7).toFixed(1)
  );
}
